// Chat.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import ScrollToBottom from "react-scroll-to-bottom";
import EmojiPicker from "emoji-picker-react";
import "./Chat.css";

const defaultRtcConfig = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

// Helper function to format time consistently
const formatTime = () => {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

// Helper function to format any timestamp to HH:MM
const formatTimestamp = (timestamp) => {
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp;
    }
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch (error) {
    return timestamp;
  }
};

function Chat({ socket, username, room, userCount, onLogout, isReconnecting }) {
  const [currentMessage, setCurrentMessage] = useState("");
  const [messageList, setMessageList] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // WebRTC states
  const [inCall, setInCall] = useState(false);
  const [callType, setCallType] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [connectedPeers, setConnectedPeers] = useState(new Map());

  // Media control states
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callDuration, setCallDuration] = useState("00:00");

  // Peer media states
  const [peerMediaStates, setPeerMediaStates] = useState(new Map());

  // Refs
  const textareaRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef(new Map());
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peerConnectionsRef = useRef(new Map());
  const callTimerRef = useRef(null);
  const rtcConfigRef = useRef(defaultRtcConfig);

  // ─── Emoji DB isolation ───────────────────────────────────────────────────
  // Generate a stable, per-user key once when the component mounts.
  // Using username + room + socket.id ensures two people with the same
  // username in different rooms — or even the same room in different tabs —
  // never share an IndexedDB store.
  const emojiDbKey = useRef(
    `spectre_${username}_${room}_${socket.id}`.replace(/[^a-zA-Z0-9_-]/g, "_"), // sanitize: IndexedDB names can't have spaces etc.
  );

  const emojiDbConfig = {
    dbName: emojiDbKey.current,
    storeName: `recent_${emojiDbKey.current}`,
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Helper function to add system messages to chat
  const addSystemMessage = useCallback(
    (message) => {
      const systemMessage = {
        room: room,
        author: "System",
        message: message,
        time: formatTime(),
        isSystem: true,
      };
      setMessageList((list) => [...list, systemMessage]);
    },
    [room],
  );

  // Auto-expand textarea
  const handleInputChange = useCallback((e) => {
    setCurrentMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = Math.min(textareaRef.current.scrollHeight, 150);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, []);

  // Update call timer
  useEffect(() => {
    if (inCall && callStartTime) {
      callTimerRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - callStartTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        setCallDuration(
          `${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`,
        );
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      setCallDuration("00:00");
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
    };
  }, [inCall, callStartTime]);

  // Fetch short-lived TURN credentials without exposing the TURN secret to clients.
  useEffect(() => {
    const controller = new AbortController();
    const signalingServerUrl =
      process.env.REACT_APP_SOCKET_URL || "https://spectrelink-backend.onrender.com";

    fetch(`${signalingServerUrl}/webrtc/turn-credentials`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `TURN credentials request failed (${response.status})`,
          );
        }
        return response.json();
      })
      .then((data) => {
        if (!Array.isArray(data.iceServers)) {
          throw new Error("TURN credentials response did not include ice servers");
        }
        rtcConfigRef.current = { iceServers: data.iceServers };
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Unable to load TURN credentials; using STUN only:", error);
        }
      });

    return () => controller.abort();
  }, []);

  const sendMessage = async () => {
    if (currentMessage.trim() !== "") {
      const messageData = {
        room: room,
        author: username,
        message: currentMessage,
        time: formatTime(),
      };

      await socket.emit("send_message", messageData);
      setMessageList((list) => [...list, messageData]);
      setCurrentMessage("");
      setShowEmojiPicker(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };

  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;

    const audioTracks = localStreamRef.current.getAudioTracks();
    setIsMuted((prev) => {
      const next = !prev;
      audioTracks.forEach((track) => {
        track.enabled = !next;
      });
      socket.emit("media_state_change", {
        room,
        user: username,
        isMuted: next,
        isVideoOff,
        isScreenSharing,
      });
      return next;
    });
  }, [room, socket, username, isVideoOff, isScreenSharing]);

  const toggleVideo = useCallback(() => {
    if (!localStreamRef.current || callType !== "video") return;

    const videoTracks = localStreamRef.current.getVideoTracks();
    setIsVideoOff((prev) => {
      const next = !prev;
      videoTracks.forEach((track) => {
        track.enabled = !next;
      });
      socket.emit("media_state_change", {
        room,
        user: username,
        isMuted,
        isVideoOff: next,
        isScreenSharing,
      });
      return next;
    });
  }, [callType, room, socket, username, isMuted, isScreenSharing]);

  const stopScreenShare = useCallback(() => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    setIsScreenSharing(false);

    const cameraVideoTrack =
      localStreamRef.current?.getVideoTracks()[0] || null;

    peerConnectionsRef.current.forEach((pc) => {
      const sender = pc
        .getSenders()
        .find((s) => s.track && s.track.kind === "video");
      if (sender) {
        sender.replaceTrack(cameraVideoTrack);
      } else if (cameraVideoTrack && localStreamRef.current) {
        pc.addTrack(cameraVideoTrack, localStreamRef.current);
      }
    });

    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }

    socket.emit("media_state_change", {
      room,
      user: username,
      isMuted,
      isVideoOff,
      isScreenSharing: false,
    });

    addSystemMessage(`${username} stopped screen sharing`);
  }, [room, socket, username, isMuted, isVideoOff, addSystemMessage]);

  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      stopScreenShare();
      return;
    }

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });
      const screenTrack = screenStream.getVideoTracks()[0];

      screenStreamRef.current = screenStream;

      screenTrack.onended = () => {
        stopScreenShare();
      };

      peerConnectionsRef.current.forEach((pc) => {
        const sender = pc
          .getSenders()
          .find((s) => s.track && s.track.kind === "video");
        if (sender) {
          sender.replaceTrack(screenTrack);
        } else if (localStreamRef.current) {
          pc.addTrack(screenTrack, localStreamRef.current);
        }
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = screenStream;
      }

      setIsScreenSharing(true);

      socket.emit("media_state_change", {
        room,
        user: username,
        isMuted,
        isVideoOff,
        isScreenSharing: true,
      });

      addSystemMessage(`${username} started screen sharing`);
    } catch (error) {
      console.error("Error starting screen share:", error);
    }
  }, [
    isScreenSharing,
    stopScreenShare,
    room,
    socket,
    username,
    isMuted,
    isVideoOff,
    addSystemMessage,
  ]);

  const handlePeerDisconnected = useCallback(
    (peerId) => {
      const pc = peerConnectionsRef.current.get(peerId);
      if (pc) {
        pc.close();
        peerConnectionsRef.current.delete(peerId);
      }

      remoteVideosRef.current.delete(peerId);
      setConnectedPeers((prev) => {
        const updated = new Map(prev);
        updated.delete(peerId);
        return updated;
      });

      setPeerMediaStates((prev) => {
        const updated = new Map(prev);
        updated.delete(peerId);
        return updated;
      });

      if (inCall && peerConnectionsRef.current.size === 0) {
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach((track) => track.stop());
          screenStreamRef.current = null;
        }
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach((track) => track.stop());
          localStreamRef.current = null;
        }
        setInCall(false);
        setCallType(null);
        setConnectedPeers(new Map());
        setPeerMediaStates(new Map());
        setCallStartTime(null);
        setIsMuted(false);
        setIsVideoOff(false);
        setIsScreenSharing(false);
      }
    },
    [inCall],
  );

  const createPeerConnection = useCallback(
    (peerId) => {
      const pc = new RTCPeerConnection(rtcConfigRef.current);

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("ice_candidate", {
            room,
            peerId,
            candidate: event.candidate,
          });
        }
      };

      pc.ontrack = (event) => {
        const [remoteStream] = event.streams;

        setConnectedPeers((prev) => {
          const updated = new Map(prev);
          updated.set(peerId, { stream: remoteStream });
          return updated;
        });

        setTimeout(() => {
          const videoElement = remoteVideosRef.current.get(peerId);
          if (videoElement && remoteStream) {
            videoElement.srcObject = remoteStream;
          }
        }, 100);
      };

      pc.oniceconnectionstatechange = () => {
        if (
          pc.iceConnectionState === "disconnected" ||
          pc.iceConnectionState === "failed"
        ) {
          handlePeerDisconnected(peerId);
        }
      };

      return pc;
    },
    [room, socket, handlePeerDisconnected],
  );

  const endCall = useCallback(() => {
    if (!inCall) return;

    const finalDuration = callDuration;
    const currentCallType = callType;

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    peerConnectionsRef.current.forEach((pc) => {
      pc.close();
    });
    peerConnectionsRef.current.clear();
    remoteVideosRef.current.clear();

    setInCall(false);
    setCallType(null);
    setConnectedPeers(new Map());
    setPeerMediaStates(new Map());
    setCallStartTime(null);
    setIsMuted(false);
    setIsVideoOff(false);
    setIsScreenSharing(false);

    if (currentCallType && finalDuration !== "00:00") {
      const callEndMessage = `${
        currentCallType.charAt(0).toUpperCase() + currentCallType.slice(1)
      } call ended. Duration: ${finalDuration}`;
      addSystemMessage(callEndMessage);
    }

    socket.emit("end_call", { room, user: username });
  }, [
    inCall,
    callDuration,
    callType,
    room,
    socket,
    username,
    addSystemMessage,
  ]);

  const handleLogout = useCallback(() => {
    setShowEmojiPicker(false);

    if (inCall) {
      endCall();
    }

    peerConnectionsRef.current.forEach((pc) => pc.close());
    peerConnectionsRef.current.clear();
    remoteVideosRef.current.clear();

    socket.disconnect();
    onLogout();
  }, [inCall, endCall, socket, onLogout]);

  const getUserMedia = async (video = false) => {
    try {
      const constraints = {
        audio: true,
        video: video ? { width: 640, height: 480 } : false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;

      if (localVideoRef.current && video) {
        localVideoRef.current.srcObject = stream;
      }

      return stream;
    } catch (error) {
      console.error("Error accessing media devices:", error);
      throw error;
    }
  };

  const startCall = async (type) => {
    try {
      const stream = await getUserMedia(type === "video");
      setCallType(type);
      setInCall(true);
      setCallStartTime(Date.now());
      setIsMuted(false);
      setIsVideoOff(false);

      if (localVideoRef.current && type === "video") {
        localVideoRef.current.srcObject = stream;
      }

      const callStartMessage = `${
        type.charAt(0).toUpperCase() + type.slice(1)
      } call started by ${username}`;
      addSystemMessage(callStartMessage);

      socket.emit("start_call", {
        room,
        callType: type,
        caller: username,
      });
    } catch (error) {
      console.error("Error starting call:", error);
      alert("Could not access camera/microphone");
    }
  };

  const joinCall = async (callData) => {
    try {
      const stream = await getUserMedia(callData.callType === "video");
      setCallType(callData.callType);
      setInCall(true);
      setIncomingCall(null);
      setCallStartTime(Date.now());
      setIsMuted(false);
      setIsVideoOff(false);

      if (localVideoRef.current && callData.callType === "video") {
        localVideoRef.current.srcObject = stream;
      }

      const joinMessage = `${username} joined the ${callData.callType} call`;
      addSystemMessage(joinMessage);

      socket.emit("join_call", {
        room,
        caller: callData.caller,
        joiner: username,
      });
    } catch (error) {
      console.error("Error joining call:", error);
      alert("Could not access camera/microphone");
    }
  };

  const declineCall = () => {
    setIncomingCall(null);
    socket.emit("decline_call", { room, user: username });
  };

  // Socket event listeners
  useEffect(() => {
    socket.off("receive_message");
    socket.off("incoming_call");
    socket.off("call_accepted");
    socket.off("offer");
    socket.off("answer");
    socket.off("ice_candidate");
    socket.off("call_declined");
    socket.off("call_ended");
    socket.off("user_left_call");
    socket.off("media_state_change");
    socket.off("media_state_changed");

    socket.on("receive_message", (data) => {
      setMessageList((list) => [...list, data]);
    });

    socket.on("incoming_call", (callData) => {
      setIncomingCall(callData);
      const incomingMessage = `Incoming ${callData.callType} call from ${callData.caller}`;
      addSystemMessage(incomingMessage);
    });

    socket.on("call_accepted", async (data) => {
      const pc = createPeerConnection(data.joiner);
      peerConnectionsRef.current.set(data.joiner, pc);

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          pc.addTrack(track, localStreamRef.current);
        });
      }

      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit("offer", {
          room,
          peerId: data.joiner,
          offer,
        });
      } catch (error) {
        console.error("Error creating offer:", error);
      }
    });

    socket.on("offer", async (data) => {
      if (!peerConnectionsRef.current.has(data.peerId)) {
        const pc = createPeerConnection(data.peerId);
        peerConnectionsRef.current.set(data.peerId, pc);

        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach((track) => {
            pc.addTrack(track, localStreamRef.current);
          });
        }
      }

      const pc = peerConnectionsRef.current.get(data.peerId);
      if (pc) {
        try {
          await pc.setRemoteDescription(data.offer);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.emit("answer", {
            room,
            peerId: data.peerId,
            answer,
          });
        } catch (error) {
          console.error("Error handling offer:", error);
        }
      }
    });

    socket.on("answer", async (data) => {
      const pc = peerConnectionsRef.current.get(data.peerId);
      if (pc) {
        try {
          await pc.setRemoteDescription(data.answer);
        } catch (error) {
          console.error("Error setting remote description:", error);
        }
      }
    });

    socket.on("ice_candidate", async (data) => {
      const pc = peerConnectionsRef.current.get(data.peerId);
      if (pc && pc.remoteDescription) {
        try {
          await pc.addIceCandidate(data.candidate);
        } catch (error) {
          console.error("Error adding ICE candidate:", error);
        }
      }
    });

    socket.on("call_declined", () => {
      setIncomingCall(null);
    });

    socket.on("call_ended", (data) => {
      if (data.user !== username) {
        addSystemMessage(`${data.user} left the call`);
        handlePeerDisconnected(data.user);
      }
    });

    socket.on("user_left_call", (data) => {
      if (data.user !== username) {
        addSystemMessage(`${data.user} left the call`);
        handlePeerDisconnected(data.user);
      }
    });

    const handleMediaStateUpdate = (data) => {
      if (data.user !== username) {
        setPeerMediaStates((prev) => {
          const updated = new Map(prev);
          updated.set(data.user, {
            isMuted: data.isMuted,
            isVideoOff: data.isVideoOff,
            isScreenSharing: data.isScreenSharing,
          });
          return updated;
        });
      }
    };

    const handleUserReconnected = (data) => {
      addSystemMessage(`${data.username} reconnected`);
    };

    socket.on("media_state_change", handleMediaStateUpdate);
    socket.on("media_state_changed", handleMediaStateUpdate);
    socket.on("user_reconnected", handleUserReconnected);

    return () => {
      socket.off("receive_message");
      socket.off("incoming_call");
      socket.off("call_accepted");
      socket.off("offer");
      socket.off("answer");
      socket.off("ice_candidate");
      socket.off("call_declined");
      socket.off("call_ended");
      socket.off("user_left_call");
      socket.off("media_state_change");
      socket.off("media_state_changed");
      socket.off("user_reconnected");
    };
  }, [
    socket,
    room,
    username,
    createPeerConnection,
    handlePeerDisconnected,
    addSystemMessage,
  ]);

  useEffect(() => {
    if (
      localStreamRef.current &&
      localVideoRef.current &&
      callType === "video"
    ) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [inCall, callType]);

  useEffect(() => {
    const peerConnections = peerConnectionsRef.current;
    const remoteVideos = remoteVideosRef.current;

    return () => {
      Object.values(peerConnections).forEach((pc) => pc.close());
      remoteVideos.forEach((video) => {
        if (video && video.srcObject) {
          video.srcObject.getTracks().forEach((track) => track.stop());
        }
      });
    };
  }, []);

  const onEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    setCurrentMessage((prev) => {
      const newMessage = prev + emoji;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
          const newHeight = Math.min(textareaRef.current.scrollHeight, 150);
          textareaRef.current.style.height = `${newHeight}px`;
        }
      }, 0);
      return newMessage;
    });
    if (textareaRef.current) textareaRef.current.focus();
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-left">
          <p>Live Chat - Room {room}</p>
          <p className="user-count">Users: {userCount}</p>
          {inCall && (
            <p className="call-status">
              {isScreenSharing ? "SCREEN SHARE" : callType === "video" ? "VIDEO" : "AUDIO"} {callDuration}
            </p>
          )}
        </div>
        <div className="chat-header-right">
          {!inCall && userCount > 1 ? (
            <>
              <button
                className="header-btn audio-btn"
                onClick={() => startCall("audio")}
              >
                <img
                  src="https://img.icons8.com/?size=100&id=9730&format=png&color=40C057"
                  alt="Audio"
                  width="20"
                  height="20"
                />
                Audio
              </button>
              <button
                className="header-btn video-btn"
                onClick={() => startCall("video")}
              >
                <img
                  src=" https://img.icons8.com/?size=100&id=11402&format=png&color=228BE6"
                  alt="Video"
                  width="20"
                  height="20"
                />
                Video
              </button>
            </>
          ) : (
            !inCall && (
              <div className="no-call-message">
                Waiting for others to join...
              </div>
            )
          )}
          {inCall && (
            <>
              <button
                className={`control-btn mute-btn ${isMuted ? "muted" : ""}`}
                onClick={toggleMute}
                title={isMuted ? "Unmute" : "Mute"}
              >
                <img
                  src={
                    isMuted
                      ? "https://img.icons8.com/?size=100&id=9976&format=png&color=FAB005"
                      : "https://img.icons8.com/?size=100&id=8LR9OAENZPeS&format=png&color=FAB005"
                  }
                  alt={isMuted ? "Muted" : "Mic"}
                  width="20"
                  height="20"
                />
              </button>
              {callType === "video" && (
                <button
                  className={`control-btn video-btn ${
                    isVideoOff ? "video-off" : ""
                  }`}
                  onClick={toggleVideo}
                  title={isVideoOff ? "Turn Video On" : "Turn Video Off"}
                >
                  <img
                    src={
                      isVideoOff
                        ? "https://img.icons8.com/?size=100&id=10343&format=png&color=228BE6"
                        : "https://img.icons8.com/?size=100&id=11402&format=png&color=228BE6"
                    }
                    alt={isVideoOff ? "Video Off" : "Video"}
                    width="20"
                    height="20"
                  />
                </button>
              )}
              <button
                className={`control-btn screen-btn ${
                  isScreenSharing ? "sharing" : ""
                }`}
                onClick={toggleScreenShare}
                title={isScreenSharing ? "Stop Screen Share" : "Share Screen"}
              >
                <img
                  src="https://img.icons8.com/?size=100&id=108205&format=png&color=228BE6"
                  alt={isScreenSharing ? "Stop Sharing" : "Screen Share"}
                  width="20"
                  height="20"
                />
              </button>
              <button className="header-btn end-call-btn" onClick={endCall}>
                <img
                  src="https://img.icons8.com/?size=100&id=20581&format=png&color=FA5252"
                  alt="End Call"
                  width="20"
                  height="20"
                />
                End Call
              </button>
            </>
          )}
          <button className="header-btn logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Video Call Interface */}
      {inCall && (
        <div className="video-call-container">
          {connectedPeers.size === 0 && (
            <div className="waiting-overlay">
              <div className="waiting-message">
                <h3>Waiting for others to join...</h3>
                <p>
                  Share room code: <strong>{room}</strong>
                </p>
                <p>Duration: {callDuration}</p>
                <button className="end-waiting-btn" onClick={endCall}>
                  End Call
                </button>
              </div>
            </div>
          )}

          <div className="call-controls">
            <div className="call-info">
              <span className="call-type-indicator">
                {isScreenSharing ? "SCREEN SHARE" : callType === "video" ? "VIDEO" : "AUDIO"}
              </span>
              <span className="call-duration">{callDuration}</span>
            </div>
            <div className="call-actions">
              <button
                className={`control-btn mute-btn ${isMuted ? "muted" : ""}`}
                onClick={toggleMute}
                title={isMuted ? "Unmute" : "Mute"}
              >
                <img
                  src={
                    isMuted
                      ? "https://img.icons8.com/?size=100&id=9976&format=png&color=FAB005"
                      : "https://img.icons8.com/?size=100&id=8LR9OAENZPeS&format=png&color=FAB005"
                  }
                  alt={isMuted ? "Muted" : "Mic"}
                  width="24"
                  height="24"
                />
              </button>
              {callType === "video" && (
                <button
                  className={`control-btn video-btn ${
                    isVideoOff ? "video-off" : ""
                  }`}
                  onClick={toggleVideo}
                  title={isVideoOff ? "Turn Video On" : "Turn Video Off"}
                >
                  <img
                    src={
                      isVideoOff
                        ? "https://img.icons8.com/?size=100&id=10343&format=png&color=228BE6"
                        : "https://img.icons8.com/?size=100&id=11402&format=png&color=228BE6"
                    }
                    alt={isVideoOff ? "Video Off" : "Video"}
                    width="24"
                    height="24"
                  />
                </button>
              )}
              <button
                className={`control-btn screen-btn ${
                  isScreenSharing ? "sharing" : ""
                }`}
                onClick={toggleScreenShare}
                title={isScreenSharing ? "Stop Screen Share" : "Share Screen"}
              >
                <img
                  src="https://img.icons8.com/?size=100&id=108205&format=png&color=228BE6"
                  alt={isScreenSharing ? "Stop Sharing" : "Screen Share"}
                  width="24"
                  height="24"
                />
              </button>
              <button
                className="control-btn end-call-btn"
                onClick={endCall}
                title="End Call"
              >
                <img
                  src="https://img.icons8.com/?size=100&id=20581&format=png&color=FA5252"
                  alt="End Call"
                  width="24"
                  height="24"
                />
              </button>
            </div>
          </div>

          <div className="local-video">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: isScreenSharing ? "320px" : "200px",
                height: isScreenSharing ? "240px" : "150px",
                objectFit: "cover",
                display:
                  (callType === "video" && !isVideoOff) || isScreenSharing
                    ? "block"
                    : "none",
              }}
            />
            {((callType === "audio" && !isScreenSharing) || (isVideoOff && !isScreenSharing)) && (
              <div className="audio-placeholder local">
                <div className="audio-avatar">
                  <span className="avatar-icon">👤</span>
                </div>
                <div className="user-status">
                  <span className="username">You</span>
                  <div className="status-badges-horizontal">
                    {isMuted && (
                      <span className="status-badge muted">Muted</span>
                    )}
                    {isVideoOff && callType === "video" && (
                      <span className="status-badge video-off">Video Off</span>
                    )}
                    {isScreenSharing && (
                      <span className="status-badge sharing">Sharing Screen</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {((callType === "video" && !isVideoOff) || isScreenSharing) && (
              <div className="video-label">
                <span>
                  You {isMuted ? "(Muted)" : ""}{" "}
                  {isScreenSharing ? "[Screen Sharing]" : ""}
                </span>
              </div>
            )}
          </div>

          <div className="remote-videos">
            {Array.from(connectedPeers.entries()).map(([peerId, peerData]) => {
              const peerMediaState = peerMediaStates.get(peerId) || {
                isMuted: false,
                isVideoOff: false,
                isScreenSharing: false,
              };

              const showVideo =
                (callType === "video" && !peerMediaState.isVideoOff) ||
                peerMediaState.isScreenSharing;

              return (
                <div key={peerId} className="remote-video">
                  <video
                    ref={(el) => {
                      if (el) {
                        remoteVideosRef.current.set(peerId, el);
                        if (peerData.stream) {
                          el.srcObject = peerData.stream;
                        }
                      }
                    }}
                    autoPlay
                    playsInline
                    style={{
                      width: peerMediaState.isScreenSharing ? "320px" : "200px",
                      height: peerMediaState.isScreenSharing ? "240px" : "150px",
                      objectFit: "cover",
                      display: showVideo ? "block" : "none",
                    }}
                  />
                  {!showVideo && (
                    <div className="audio-placeholder remote">
                      <div className="audio-avatar">
                        <span className="avatar-icon">👤</span>
                      </div>
                      <div className="user-status">
                        <span className="username">{peerId}</span>
                        <div className="status-badges-horizontal">
                          {peerMediaState.isMuted && (
                            <span className="status-badge muted">Muted</span>
                          )}
                          {peerMediaState.isVideoOff &&
                            callType === "video" && (
                              <span className="status-badge video-off">
                                Video Off
                              </span>
                            )}
                          {peerMediaState.isScreenSharing && (
                            <span className="status-badge sharing">
                              Sharing Screen
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {showVideo && (
                    <div className="video-label">
                      <span>
                        {peerId} {peerMediaState.isMuted ? "(Muted)" : ""}{" "}
                        {peerMediaState.isScreenSharing ? "[Screen Sharing]" : ""}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Incoming Call Modal */}
      {incomingCall && (
        <div className="incoming-call-modal">
          <div className="modal-content">
            <h3>Incoming {incomingCall.callType} call</h3>
            <p>From: {incomingCall.caller}</p>
            <div className="modal-buttons">
              <button
                className="accept-btn"
                onClick={() => joinCall(incomingCall)}
              >
                Accept
              </button>
              <button className="decline-btn" onClick={declineCall}>
                Decline
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chat-body">
        <ScrollToBottom className="message-container">
          {messageList.map((messageContent, index) => (
            <div
              key={index}
              className={`message ${
                messageContent.isSystem
                  ? "system"
                  : username === messageContent.author
                    ? "you"
                    : "other"
              }`}
            >
              <div>
                <div className="message-content">
                  <p>{messageContent.message}</p>
                </div>
                <div className="message-meta">
                  <p>
                    <span className="author">
                      {messageContent.isSystem
                        ? "System"
                        : username === messageContent.author
                          ? "You"
                          : messageContent.author}
                    </span>
                    <span style={{ margin: "0 8px" }}>|</span>
                    <span className="time">
                      {formatTimestamp(messageContent.time)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </ScrollToBottom>
      </div>

      <div className="chat-footer">
        <div className="emoji-section">
          <button
            className="emoji-toggle"
            onClick={() => setShowEmojiPicker((prev) => !prev)}
          >
            😊
          </button>
          {showEmojiPicker && (
            <div className="emoji-picker-container">
              <EmojiPicker
                onEmojiClick={onEmojiClick}
                height={380}
                searchDisabled={true}
                skinTonesDisabled={true}
                previewConfig={{
                  showPreview: true,
                  defaultEmoji: "1f60a",
                  defaultCaption: "Choose an emoji",
                }}
                // Each user gets their own isolated IndexedDB store.
                // Key = username + room + socket.id → guaranteed unique
                // even if two people share the same username across rooms
                // or open the app in two tabs simultaneously.
                lazyLoadEmojis={true}
                dbConfig={emojiDbConfig}
                style={{
                  "--epr-category-label-height": "0px",
                }}
              />
            </div>
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={currentMessage}
          placeholder="Type a message"
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />

        <button onClick={sendMessage}>Send</button>
      </div>
    </div>
  );
}

export default Chat;
